/**
 * PreparedDropsUndoButton — one step back on the drafts toolbar.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Hidden until there is something to undo, so the toolbar is unchanged in
 * normal use. It confirms first and says exactly what will happen: undo now
 * covers edits and duplicates as well as deletes, and "revert an edit" is not
 * something to trigger on a stray click.
 *
 * Self-contained on purpose — it fetches its own status, so mounting it costs
 * the toolbar one line and no new props.
 */

import { type FC, useState } from 'react';
import { Undo2 } from 'lucide-react';

import { Button, ConfirmDialog } from '@/components/ui';
import { useToast } from '@/providers/ToastProvider';

import {
  usePreparedDropsUndo,
  type PreparedDropsUndoOp,
  type PreparedDropsUndoResult,
} from './usePreparedDropsUndo';

const plural = (n: number) => (n === 1 ? '' : 's');

const describe = (
  op: PreparedDropsUndoOp | undefined,
  count: number,
  countries: string[],
): string => {
  const where = countries.length > 1 ? ` across ${countries.join(', ')}` : '';
  if (op === 'delete') {
    return `Restore ${count} deleted draft${plural(count)}${where}?`;
  }
  if (op === 'create') {
    return `Remove the ${count} draft${plural(count)} that were just added${where}?`;
  }
  if (op === 'remote-edit') {
    // Names SMSWiser explicitly: this writes to the live scheduled drop, not
    // just to our copy of it.
    return `Put back the previous sender, texts and title on ${count} scheduled drop${plural(count)} in SMSWiser${where}?`;
  }
  if (op === 'send') {
    // The one confirm that must not read like a plain undo: stopping halts what
    // is left to go out, it does not recall what already went.
    return (
      `Stop ${count} drop${plural(count)} in SMSWiser and return ${count === 1 ? 'it' : 'them'} to draft${where}? ` +
      'Messages already sent cannot be recalled, so sending again would reach those people twice.'
    );
  }
  if (op === 'resend') {
    return (
      `Stop the ${count} re-sent drop${plural(count)} in SMSWiser and remove ${count === 1 ? 'it' : 'them'}${where}? ` +
      `The killed drop${count === 1 ? ' stays' : 's stay'} as ${count === 1 ? 'it was' : 'they were'}. Messages already sent cannot be recalled.`
    );
  }
  if (op === 'stage-draft') {
    // Says "in SMSWiser" on purpose: this one reaches outside the app.
    return `Delete the ${count} draft${plural(count)} just staged in SMSWiser${where}?`;
  }
  return `Revert the last edit to ${count} draft${plural(count)}${where}?`;
};

/** What actually happened — a partial restore must say so, not read as success. */
const outcome = (r: PreparedDropsUndoResult): string => {
  const missed = r.skipped + r.skippedMissingCampaign;
  if (missed === 0) return '';
  const gone = r.skippedMissingCampaign
    ? ` (${r.skippedMissingCampaign} whose campaign no longer exists)`
    : '';
  return `${missed} could not be restored${gone}.`;
};

export const PreparedDropsUndoButton: FC = () => {
  const { status, undo } = usePreparedDropsUndo();
  const [open, setOpen] = useState(false);
  const toast = useToast();

  if (!status.available && !undo.isPending) return null;

  const run = () =>
    undo.mutate(undefined, {
      onSuccess: (result) => {
        setOpen(false);
        const detail = outcome(result);
        if (detail) toast.warning(`Undone — ${result.restored} reverted`, detail);
        else toast.success(`Undone — ${result.restored} reverted`);
      },
      onError: () => {
        setOpen(false);
        toast.error('Could not undo', 'Nothing was changed — try again.');
      },
    });

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        loading={undo.isPending}
        onClick={() => setOpen(true)}
        aria-label="Undo last change to prepared drops"
      >
        <Undo2 className="h-4 w-4" />
        Undo last change
        <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium tabular-nums">
          {status.dropCount}
        </span>
      </Button>
      <ConfirmDialog
        open={open}
        title="Undo last change"
        description={describe(status.op, status.dropCount, status.countryKeys)}
        confirmLabel="Undo"
        busy={undo.isPending}
        onConfirm={run}
        onCancel={() => setOpen(false)}
      />
    </>
  );
};

export default PreparedDropsUndoButton;
